﻿(() => {
  if (customElements.get('product-reviews-carousel')) return;
  customElements.define('product-reviews-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.visibilityObserver) return;
      if (!('IntersectionObserver' in window)) { this.initialize(); return; }
      this.visibilityObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
        this.initialize();
      }, { rootMargin: '300px' });
      this.visibilityObserver.observe(this);
    }
    initialize() {
      if (this.controller || !this.isConnected) return;
      const track = this.querySelector('.prc__track');
      if (!track) return;
      const cards = [...track.querySelectorAll('.prc__card')];
      if (!cards.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const previous = this.querySelector('[data-prc-prev]');
      const next = this.querySelector('[data-prc-next]');
      this.clones = [];
      const duplicate = card => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        this.clones.push(clone);
        return clone;
      };
      track.prepend(...cards.map(duplicate));
      track.append(...cards.map(duplicate));
      let step = 0;
      let span = 0;
      let dragging = false;
      let target = null;
      const jump = left => {
        const snap = track.style.scrollSnapType;
        const behavior = track.style.scrollBehavior;
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = left;
        track.getBoundingClientRect();
        track.style.scrollSnapType = snap;
        track.style.scrollBehavior = behavior;
      };
      const normalize = () => {
        clearTimeout(this.settleTimer);
        if (!span || dragging) return;
        const left = track.scrollLeft;
        const centered = left < span - 1 ? left + span : left >= span * 2 - 1 ? left - span : left;
        target = null;
        if (Math.abs(centered - left) > 1) jump(centered);
      };
      const update = () => {
        const index = step ? (track.scrollLeft - span) / step : 0;
        const newStep = cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
        if (!newStep || !track.clientWidth) return;
        const changed = Math.abs(newStep - step) > 0.01;
        step = newStep;
        span = step * cards.length;
        const hasOverflow = span - (parseFloat(getComputedStyle(track).gap) || 0) > track.clientWidth + 1;
        if (previous) previous.disabled = !hasOverflow;
        if (next) next.disabled = !hasOverflow;
        if (changed) {
          target = null;
          jump(span + ((index % cards.length + cards.length) % cards.length) * step);
        }
      };
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-prc-prev],[data-prc-next]');
        if (!button || button.disabled || !step) return;
        const direction = button.hasAttribute('data-prc-prev') ? -1 : 1;
        target = (target ?? track.scrollLeft) + direction * step;
        target = Math.max(0, Math.min(track.scrollWidth - track.clientWidth, target));
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
          jump(target);
          normalize();
        } else {
          track.scrollTo({ left: target, behavior: 'smooth' });
        }
      }, { signal });
      track.addEventListener('scroll', () => {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(normalize, 160);
      }, { passive: true, signal });
      track.addEventListener('scrollend', normalize, { signal });
      track.addEventListener('pointerdown', () => { dragging = true; target = null; }, { passive: true, signal });
      const release = () => {
        if (!dragging) return;
        dragging = false;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(normalize, 160);
      };
      window.addEventListener('pointerup', release, { passive: true, signal });
      window.addEventListener('pointercancel', release, { passive: true, signal });
      track.addEventListener('wheel', () => { target = null; }, { passive: true, signal });
      this.observer = new ResizeObserver(update);
      this.observer.observe(track);
      update();
    }
    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearTimeout(this.settleTimer);
      this.clones?.forEach(clone => clone.remove());
      this.clones = [];
    }
  });
})();
